import { isEmpty } from "lodash";
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { InspectionImagesCount } from "../../_objects/inspection";
import {
  InspectionImage,
  InspectionImageCreate,
  InspectionImageListItem,
  InspectionImageMultipleUpdate,
  InspectionImageOrderChange,
  InspectionImageUpdate,
  MeasurementInfo,
  ModifiedImageUseUpdate,
  ReadImageOption
} from "../../_objects/inspection-image";
import { TenantDataFile } from "../../_objects/tenant-data";
import { CustomEncoder } from "../../_shared/custom-encoder";
import { DEFAULT_SORT_SETTING, PLATFORM } from "../../app.constants";
import { HttpService } from "../http.service";
import { InspectionImageContractService } from "../interfaces/inspection-image-contract.service";
import { CreateImagesResponse } from "../responses/inspection-image.response";
import { InspectionImageFilters } from "../../_objects/inspection-image-filters";
import { ImagesFiltersStateService } from "../filter-state.service";
import { NetworkStatusService } from "./../network-status.service";

@Injectable({
  providedIn: "root"
})
export class InspectionImageOnlineService extends HttpService implements InspectionImageContractService {
  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private filterStateService: ImagesFiltersStateService,
    private networkStatusService: NetworkStatusService
  ) {
    super();
    super.setCookieService(cookieService);
    super.setHttpClient(http);
    super.setRouter(router);
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  // IM01 GET /api/v1/inspections/{inspectionId}/images
  getInspectionImages(inspectionId: string, filters: InspectionImageFilters): Observable<InspectionImageListItem[]> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images");
    let httpParams = new HttpParams({ encoder: new CustomEncoder() });
    if (this.filterStateService.inspectionImagesLocationFilters) {
      this.filterStateService.inspectionImagesLocationFilters.forEach((locationId) => {
        httpParams = httpParams.append("lid", locationId);
      });
    }
    if (!isEmpty(filters?.marks)) {
      filters.marks.forEach((mark) => {
        httpParams = httpParams.append("mk", mark.toString());
      });
    }
    if (filters?.printScreens) {
      filters.printScreens.forEach((printScreen) => {
        httpParams = httpParams.append("psf", printScreen.toString());
      });
    }

    if (filters?.sort && filters?.sortOrder) {
      httpParams = httpParams.append("sort", filters.sort.toLowerCase());
      httpParams = httpParams.append("sortOrder", filters.sortOrder);
    } else {
      httpParams = httpParams.append("sort", DEFAULT_SORT_SETTING.SORT.toLowerCase());
      httpParams = httpParams.append("sortOrder", DEFAULT_SORT_SETTING.SORTORDER);
    }

    return this.http
      .get<InspectionImageListItem[]>(endpoint, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as InspectionImageListItem[]));
  }

  // IM02 GET /api/v1/inspections/{inspectionId}/images/{inspectionImageId}
  getInspectionImage(inspectionId: string, inspectionImageId: string): Observable<InspectionImage> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images", inspectionImageId);
    return this.http
      .get<InspectionImage>(endpoint, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as InspectionImage));
  }

  // IM03 POST /api/v1/inspections/{inspectionId}/images
  fileUploadRaw(inspectionImageCreate: InspectionImageCreate, file: File, fullPath: string): Observable<HttpEvent<{}>> {
    const formData = new FormData();
    let httpParams = new HttpParams();
    formData.append("file", file, file.name);

    if (fullPath) {
      const filePath = fullPath.split("/").reverse();

      if (filePath.length >= 2) {
        httpParams = httpParams.append("fn", filePath[1]);
      }
    }

    if (inspectionImageCreate?.locationName) {
      httpParams = httpParams.append("ln", inspectionImageCreate.locationName);
    }

    if (inspectionImageCreate?.folderPath) {
      httpParams = httpParams.append("fp", inspectionImageCreate.folderPath);
    }

    const endpoint = super.getEndpoint("inspections", inspectionImageCreate.inspectionId, "images");

    const req = new HttpRequest("POST", endpoint, formData, {
      withCredentials: super.getWithCredentialsOption(),
      params: httpParams,
      reportProgress: true
    });
    return this.http.request(req);
  }

  // IM04 PUT /api/v1/inspections/{inspectionId}/images/{inspectionImageId}
  patchInspectionImage(inspectionImageUpdate: InspectionImageUpdate): Observable<any> {
    const endpoint = super.getEndpoint("inspections", inspectionImageUpdate.inspectionId, "images", inspectionImageUpdate.id);

    return this.http.put<InspectionImageUpdate>(endpoint, inspectionImageUpdate, {
      withCredentials: super.getWithCredentialsOption(),
      params: new HttpParams()
    });
  }

  // IM05 PUT /api/v1/inspections/{inspectionId}/images
  patchInspectionImageMultiple(inspectionId: string, inspectionImageMultipleUpdate: InspectionImageMultipleUpdate): Observable<any> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images");

    return this.http.put<InspectionImageMultipleUpdate>(endpoint, inspectionImageMultipleUpdate, {
      withCredentials: super.getWithCredentialsOption(),
      params: new HttpParams()
    });
  }

  // IM06 DELETE /api/v1/inspections/{inspectionId}/images/{inspectionImageId}
  deleteInspectionImage(inspectionId: string, inspectionImageId: string): Observable<any> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images", inspectionImageId);

    return this.http.delete(endpoint, {
      withCredentials: super.getWithCredentialsOption(),
      params: new HttpParams()
    });
  }

  // IM07 DELETE /api/v1/inspections/{inspectionId}/images
  deleteInspectionImageMultiple(inspectionId: string, inspectionImageIdDeleteList: string[]): Observable<any> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images");
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append("Content-Type", "application/json");
    headers = headers.append("responseType", "json");

    return this.http.request("DELETE", endpoint, {
      body: inspectionImageIdDeleteList,
      responseType: "json",
      headers,
      withCredentials: super.getWithCredentialsOption(),
      params: new HttpParams()
    });
  }

  // IM08 PUT /api/v1/inspections/{inspectionId}/images/sortOrder
  patchInspectionImageOrderChange(inspectionId: string, imageOrders: InspectionImageOrderChange): Observable<InspectionImageListItem[]> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images", "sortOrder");

    return this.http.put<InspectionImageListItem[]>(endpoint, imageOrders, {
      withCredentials: super.getWithCredentialsOption(),
      params: new HttpParams()
    });
  }

  // IM09 PUT /api/v1/inspections/{inspectionId}/images/{inspectionImageId}/usedFlag
  putInspImageModifiedUse(modifiedUse: ModifiedImageUseUpdate): Observable<InspectionImage> {
    const endpoint = super.getEndpoint("inspections", modifiedUse.inspectionId, "images", modifiedUse.id, "usedFlag");

    return this.http.put<InspectionImage>(endpoint, modifiedUse, { withCredentials: super.getWithCredentialsOption(), params: new HttpParams() });
  }

  // IM10 /api/image/file
  async getImageBinaryUrl(inspectionId: string, inspectionImageId: string, imageMode: number = 0): Promise<string> {
    const endpoint = super.getEndpoint("image", "file");
    const url = new URL(endpoint);
    url.searchParams.append("inspectionId", inspectionId);
    url.searchParams.append("inspectionImageId", inspectionImageId);
    url.searchParams.append("imageMode", imageMode.toString());

    return url.toString();
  }

  // /api/inspections/{inspectionId}/createImages
  createInspectionImages(inspectionId: string, inspectionImagesForCreate: InspectionImageCreate[]) {
    const endpoint = super.getEndpoint("inspections", inspectionId, "createImages");

    return this.http.post<CreateImagesResponse>(endpoint, inspectionImagesForCreate, {
      withCredentials: this.getWithCredentialsOption()
    });
  }

  // IM12 /api/inspections/{inspectionId}/images/{inspectionImageId}/read
  readInspectionImage(inspectionId: string, inspectionImageId: string, readImageOption: ReadImageOption): Observable<InspectionImage> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images", inspectionImageId, "read");

    let httpParams = new HttpParams();
    httpParams = httpParams.append("readImageOption", readImageOption);

    return this.http
      .put<InspectionImage>(endpoint, null, {
        withCredentials: super.getWithCredentialsOption(),
        params: httpParams
      })
      .pipe(map((response) => response));
  }

  // IM13 /api/inspections/{inspectionId}/images/{inspectionImageId}/measurementdata
  getInspectionImageMeasurementData(inspectionId: string, inspectionImageId: string): Observable<MeasurementInfo> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images", inspectionImageId, "measurementdata");

    return this.http.get<MeasurementInfo>(endpoint, { withCredentials: super.getWithCredentialsOption() });
  }

  // /api/inspections/{inspectionId}/images/{inspectionImageId}/file
  updateInspectionImageFile(inspectionId: string, inspectionImageId: string, file: File): Observable<void> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images", inspectionImageId, "file");
    const formData = new FormData();
    formData.append("file", file, file.name);

    return this.http.put<void>(endpoint, formData, { withCredentials: super.getWithCredentialsOption() });
  }

  // IM14 GET /api/inspections/{inspectionId}/images/count/total
  getImageCountOfInspection(inspectionId: string): Observable<InspectionImagesCount> {
    const endpoint = super.getEndpoint("inspections", inspectionId, "images", "count", "total");

    return this.http.get<InspectionImagesCount>(endpoint, { withCredentials: super.getWithCredentialsOption() });
  }

  // IM14 POST /api/inspections/images/count
  getImageCountForInspections(inspectionIds: string[]): Observable<any> {
    const endpoint = super.getEndpoint("inspections", "images", "count");
    return this.http.post<InspectionImagesCount>(endpoint, inspectionIds, { withCredentials: super.getWithCredentialsOption() });
  }

  // ----------------------------------------

  convertInspectionImage2InpsctionImageUpdate(inspectionImage: InspectionImage): InspectionImageUpdate {
    const inspectionImageUpdate = new InspectionImageUpdate();
    inspectionImageUpdate.id = inspectionImage.id;
    inspectionImageUpdate.inspectionId = inspectionImage.inspectionId;
    inspectionImageUpdate.subjectLocationId = inspectionImage.subjectLocationId;
    inspectionImageUpdate.imageDescription = inspectionImage.imageDescription;
    inspectionImageUpdate.comment = inspectionImage.comment;
    inspectionImageUpdate.metadata = inspectionImage.metadata;
    inspectionImageUpdate.schemaVersion = inspectionImage.schemaVersion;

    return inspectionImageUpdate;
  }

  convertInspectionImage2InspectionImageListItem(inspectionImage: InspectionImage): InspectionImageListItem {
    const inspectionImageListItem = new InspectionImageListItem();
    inspectionImageListItem.id = inspectionImage.id;
    inspectionImageListItem.inspectionId = inspectionImage.inspectionId;
    inspectionImageListItem.imageDescription = inspectionImage.imageDescription;
    inspectionImageListItem.comment = inspectionImage.comment;
    inspectionImageListItem.datetime = inspectionImage.datetime;
    inspectionImageListItem.fileName = inspectionImage.fileName;
    inspectionImageListItem.mimeType = inspectionImage.mimeType;
    inspectionImageListItem.subjectLocationId = inspectionImage.subjectLocationId;
    inspectionImageListItem.title = inspectionImage.metadata.title;
    inspectionImageListItem.mark = inspectionImage.metadata.mark;
    inspectionImageListItem.thumbFilePath = inspectionImage.thumbFilePath;

    return inspectionImageListItem;
  }
}
